import React from 'react';
import type { ChangeEvent, ReactElement, ReactNode } from 'react';
import clsx from 'clsx';
import { useController } from 'react-hook-form';
import type { Control, FieldPath, FieldValues } from 'react-hook-form';

type OptionProps = {
  id: string;
  value: string;
  children: ReactNode;
};

function Option({ id, value, children }: OptionProps): ReactElement {
  return (
    <option value={value} id={id}>
      {children}
    </option>
  );
}

type SelectProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> = {
  id: string;
  ariaLabel: string; // Normally matches the select's <label>
  control: Control<TFieldValues>;
  name: TName;
  className?: string;
  size?: number;
  disabled?: boolean;
  onChange?: (event: ChangeEvent<HTMLSelectElement>) => void;
  children: ReactNode; // The select's <option> elements
};

function Select<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({
  id,
  ariaLabel,
  control,
  name,
  className,
  size,
  disabled,
  onChange,
  children
}: SelectProps<TFieldValues, TName>): ReactElement {
  const { field } = useController({ control, name });
  return (
    <select
      {...field}
      id={id}
      className={clsx('form-select', className)}
      size={size}
      aria-label={ariaLabel}
      disabled={disabled}
      onChange={(event: ChangeEvent<HTMLSelectElement>): void => {
        field.onChange(event);
        if (onChange) {
          onChange(event);
        }
      }}
    >
      {children}
    </select>
  );
}

/**
 * These components are intended to represent a minimal HTML <select> element
 * and it's options without search capabilities. If you need a more advanced
 * select input, use the Combo Box or Multiple Combo Box components.
 */
export default Object.assign(Select, {
  Option
});
